/**
 * =============================================================================
 * Componente: ReferralPaywall
 * Versão: 1.1.0 (2026-05-04)
 *
 * Descrição:
 *   Tela de bloqueio do módulo Indicação. Apresenta os 3 tiers pagos +
 *   pacote one-time de créditos de conversão.
 *
 * Mudanças v1.1.0:
 *   - Adicionado modal de seleção de quantidade ao comprar pacote de créditos
 *     (espelha padrão de Plans.tsx). Resolve o problema de popup bloqueado
 *     quando window.open era chamado dentro de Dialog Radix sem gesto direto.
 *   - Compra do pacote de créditos só é habilitada para tenants em tier pago
 *     (referral_starter | referral_pro | referral_unlimited). Tenants em tier
 *     free veem o botão desabilitado com tooltip orientando contratar plano.
 * =============================================================================
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useReferralAccess } from '@/hooks/useReferralAccess';
import { createLogger } from '@/lib/logger';
import { Check, Loader2, Sparkles, Minus, Plus, Lock } from 'lucide-react';

const log = createLogger('ReferralPaywall');

// Slugs definidos no seed da migration de cobrança Indicação
const TIER_SLUGS = ['referral_starter', 'referral_pro', 'referral_unlimited'] as const;
const CREDITS_PACK_SLUG = 'referral_conversion_credits_pack';

interface PlanAddon {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  price_monthly: number;
  addon_type: string;
  max_referral_campaigns: number | null;
  max_referral_programs: number | null;
  max_referral_affiliates: number | null;
  referral_bonus_credits: number;
  conversion_credit_quota: number;
}

interface ReferralPaywallProps {
  /** Título contextual (ex: "Você atingiu o limite de campanhas") */
  title?: string;
  /** Subtítulo opcional explicando o motivo do bloqueio */
  subtitle?: string;
}

export function ReferralPaywall({
  title = 'Desbloqueie o módulo Indicação',
  subtitle,
}: ReferralPaywallProps) {
  const { currentTenant } = useAuth();
  const { toast } = useToast();
  const { data: accessData } = useReferralAccess();
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);

  // Estado do modal de quantidade do pacote de créditos
  const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);

  // Gate: pacote de créditos só pode ser comprado por tenants em tier pago
  const canBuyCredits = accessData?.is_paid_tier === true;

  // Busca add-ons via RPC server-side (get_available_addons_safe v1.1.0).
  // Não consultamos `plan_addons` diretamente — toda leitura passa por RPC
  // por segurança (RLS bypass controlado + auditoria) e performance (cache).
  const { data: addons, isLoading } = useQuery({
    queryKey: ['referral-paywall-addons'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_available_addons_safe');
      if (error) throw error;
      const allowed = new Set<string>([...TIER_SLUGS, CREDITS_PACK_SLUG]);
      const rows = (data || []) as Array<{
        addon_id_full: string;
        slug: string;
        name: string;
        description: string | null;
        price_monthly: number;
        addon_type: string;
        max_referral_campaigns: number | null;
        max_referral_programs: number | null;
        max_referral_affiliates: number | null;
        referral_bonus_credits: number | null;
        conversion_credit_quota: number | null;
      }>;
      return rows
        .filter((r) => allowed.has(r.slug))
        .map<PlanAddon>((r) => ({
          id: r.addon_id_full,
          slug: r.slug,
          name: r.name,
          description: r.description,
          price_monthly: Number(r.price_monthly ?? 0),
          addon_type: r.addon_type,
          max_referral_campaigns: r.max_referral_campaigns,
          max_referral_programs: r.max_referral_programs,
          max_referral_affiliates: r.max_referral_affiliates,
          referral_bonus_credits: Number(r.referral_bonus_credits ?? 0),
          conversion_credit_quota: Number(r.conversion_credit_quota ?? 0),
        }))
        .sort((a, b) => a.price_monthly - b.price_monthly);
    },
    staleTime: 60_000,
  });

  const tiers = (addons || []).filter((a) => a.addon_type === 'referral_tier');
  const creditsPack = (addons || []).find((a) => a.slug === CREDITS_PACK_SLUG);

  /**
   * Inicia checkout de um add-on. Tiers usam quantity=1; pacote de créditos
   * pode usar quantidade selecionada no modal.
   */
  const handleCheckout = async (addon: PlanAddon, quantity = 1) => {
    if (!currentTenant?.id) {
      toast({ title: 'Tenant não identificado', variant: 'destructive' });
      return;
    }
    setLoadingSlug(addon.slug);
    try {
      const { data, error } = await supabase.functions.invoke('addon-checkout', {
        body: {
          tenant_id: currentTenant.id,
          addon_id: addon.id,
          quantity,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        // Fechar dialog ANTES de abrir nova aba garante que window.open seja
        // tratado como gesto direto do usuário (evita bloqueio de popup)
        setQuantityDialogOpen(false);
        window.open(data.url, '_blank');
      } else {
        throw new Error('URL de checkout não retornada');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      log.error('Erro ao iniciar checkout de Indicação', { slug: addon.slug, error: msg });
      toast({ title: 'Erro ao abrir checkout', description: msg, variant: 'destructive' });
    } finally {
      setLoadingSlug(null);
    }
  };

  /** Abre modal de quantidade do pacote de créditos */
  const handleOpenCreditsModal = () => {
    setPurchaseQuantity(1);
    setQuantityDialogOpen(true);
  };

  const formatBRL = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="mx-auto max-w-6xl px-4 py-8 md:py-12">
        <div className="mb-8 text-center">
          <Badge className="mb-3" variant="secondary">
            <Sparkles className="mr-1 h-3 w-3" /> Módulo Indicação
          </Badge>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">{title}</h1>
          {subtitle && <p className="mt-2 text-muted-foreground">{subtitle}</p>}
          <p className="mt-3 text-sm text-muted-foreground">
            Você está no plano gratuito (1 campanha, 1 programa, 10 afiliados, 20 conversões inclusas).
            Para liberar mais recursos, escolha um dos planos abaixo.
          </p>
        </div>

        {/* Tiers de assinatura */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {tiers.map((tier, idx) => {
            const featured = idx === 1; // Pro destacado
            return (
              <Card
                key={tier.id}
                className={featured ? 'border-primary shadow-lg' : ''}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{tier.name}</CardTitle>
                    {featured && <Badge>Mais popular</Badge>}
                  </div>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">{formatBRL(tier.price_monthly)}</span>
                    <span className="text-sm text-muted-foreground">/mês</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        {tier.max_referral_campaigns === null
                          ? 'Campanhas ilimitadas'
                          : `${tier.max_referral_campaigns} campanha(s)`}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        {tier.max_referral_programs === null
                          ? 'Programas ilimitados'
                          : `${tier.max_referral_programs} programa(s)`}
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <span>
                        {tier.max_referral_affiliates === null
                          ? 'Afiliados ilimitados'
                          : `Até ${tier.max_referral_affiliates.toLocaleString('pt-BR')} afiliados`}
                      </span>
                    </li>
                    {tier.referral_bonus_credits > 0 && (
                      <li className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>
                          {tier.referral_bonus_credits} créditos de conversão inclusos
                        </span>
                      </li>
                    )}
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        Créditos extras via pacote avulso
                      </span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleCheckout(tier, 1)}
                    disabled={loadingSlug === tier.slug}
                    className="w-full"
                    variant={featured ? 'default' : 'outline'}
                  >
                    {loadingSlug === tier.slug ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Contratar'
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Pacote de créditos one-time — habilitado apenas para tier pago */}
        {creditsPack && (
          <Card className="mt-6 border-dashed">
            <CardContent className="flex flex-col items-start justify-between gap-4 py-5 md:flex-row md:items-center">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{creditsPack.name}</h3>
                  {!canBuyCredits && (
                    <Badge variant="secondary" className="gap-1">
                      <Lock className="h-3 w-3" /> Requer plano pago
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Compra avulsa: +{creditsPack.conversion_credit_quota} créditos de conversão.
                </p>
                {!canBuyCredits && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Disponível apenas para assinantes dos planos Indicação Starter, Pro ou Unlimited.
                    Contrate um plano acima para liberar a compra de pacotes de créditos.
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-2xl font-bold">{formatBRL(creditsPack.price_monthly)}</span>
                {canBuyCredits ? (
                  <Button onClick={handleOpenCreditsModal} variant="secondary">
                    Comprar pacote
                  </Button>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={0}>
                        <Button variant="secondary" disabled className="pointer-events-none">
                          <Lock className="mr-1 h-3.5 w-3.5" />
                          Comprar pacote
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <p className="text-xs">
                        Contrate um dos planos Indicação (Starter, Pro ou Unlimited) para
                        liberar a compra de pacotes de créditos de conversão.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal de seleção de quantidade do pacote de créditos */}
      {creditsPack && (
        <Dialog open={quantityDialogOpen} onOpenChange={setQuantityDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Adicionar {creditsPack.name}</DialogTitle>
              <DialogDescription>
                {creditsPack.description ||
                  `${creditsPack.conversion_credit_quota} créditos de conversão para o módulo Indicação.`}
              </DialogDescription>
            </DialogHeader>

            <div className="py-6 space-y-6">
              {/* Seletor de Quantidade */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Quantidade</span>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setPurchaseQuantity(Math.max(1, purchaseQuantity - 1))}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-10 text-center font-semibold text-lg">
                    {purchaseQuantity}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => setPurchaseQuantity(purchaseQuantity + 1)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Resumo de Custo */}
              <div className="rounded-xl bg-muted p-5 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Valor mensal</span>
                  <span className="font-medium">
                    {formatBRL(creditsPack.price_monthly * purchaseQuantity)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-base font-semibold">
                  <span>Total hoje</span>
                  <span>{formatBRL(creditsPack.price_monthly * purchaseQuantity)}</span>
                </div>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setQuantityDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                onClick={() => handleCheckout(creditsPack, purchaseQuantity)}
                disabled={loadingSlug === creditsPack.slug}
              >
                {loadingSlug === creditsPack.slug && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Confirmar Compra
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </TooltipProvider>
  );
}
